import { Link } from "react-router-dom";
import { Heart, Send } from "lucide-react";
import JobDetailCompanyLinks, { jobHasCompanyLinks } from "./JobDetailCompanyLinks";
import JobHighlightPills from "./JobHighlightPills";
import JobShareMenu from "./JobShareMenu";

/**
 * Job detail top block: title row with brand top-right, then toolbar + meta cards full width.
 * The bottom floating dock is unchanged and lives in JobDetailsPage.
 */
export default function JobDetailHeader({
  job,
  currentUser,
  guestSignInHref,
  canApplyForRole,
  canSetJobInteraction,
  jobPostInteraction,
  jobPostInteractionLoaded,
  userHasAppliedToRole,
  applyInteractionPending,
  isApplicantProfileIncomplete,
  onApplyClick,
  onSaveClick,
  onProfileIncomplete,
}) {
  const isSignedIn = Boolean(currentUser);
  const isSaved = Boolean(jobPostInteraction?.saved);
  const applyDisabled =
    isSignedIn && (userHasAppliedToRole || !canApplyForRole || applyInteractionPending);
  const saveDisabled = isSignedIn && (!canSetJobInteraction || !jobPostInteractionLoaded);

  const handleApply = () => {
    if (!isSignedIn) return;
    if (!canApplyForRole || applyInteractionPending || userHasAppliedToRole) return;
    if (isApplicantProfileIncomplete) {
      onProfileIncomplete?.();
      return;
    }
    onApplyClick?.();
  };

  const handleSave = () => {
    if (!isSignedIn) return;
    if (saveDisabled) return;
    onSaveClick?.();
  };

  const applyLabel = userHasAppliedToRole && isSignedIn ? "Applied" : "Apply now";
  const showHeroBrand = Boolean(job.companyLogoUrl || jobHasCompanyLinks(job));

  return (
    <header className="nd1-job-detail__header">
      <div className={`nd1-job-detail__intro${showHeroBrand ? "" : " nd1-job-detail__intro--solo"}`}>
        <div className="nd1-job-detail__hero-main job-detail-head-main">
          <h2>{job.title}</h2>
          {job.company ? <p className="job-company">{job.company}</p> : null}
        </div>

        {showHeroBrand ? (
          <aside className="nd1-job-detail__header-brand" aria-label="Company">
            {job.companyLogoUrl ? (
              <div className="nd1-job-detail__brand-logo-wrap">
                <img
                  className="job-detail-company-logo"
                  src={job.companyLogoUrl}
                  alt={`${job.company || "Company"} logo`}
                  loading="lazy"
                />
              </div>
            ) : null}
            <JobDetailCompanyLinks job={job} stacked />
          </aside>
        ) : null}
      </div>

      <div className="nd1-job-detail__toolbar" aria-label="Job actions">
        {isSignedIn ? (
          <button
            type="button"
            className="nd1-job-detail__toolbar-btn nd1-btn nd1-btn--primary nd1-job-detail__toolbar-apply"
            disabled={applyDisabled}
            onClick={handleApply}
          >
            <Send className="nd1-icon" size={18} strokeWidth={1.75} aria-hidden="true" />
            <span>{applyLabel}</span>
          </button>
        ) : (
          <Link
            to={guestSignInHref}
            className="nd1-job-detail__toolbar-btn nd1-btn nd1-btn--primary nd1-job-detail__toolbar-apply"
          >
            <Send className="nd1-icon" size={18} strokeWidth={1.75} aria-hidden="true" />
            <span>Apply now</span>
          </Link>
        )}

        {isSignedIn ? (
          <button
            type="button"
            className={`nd1-job-detail__toolbar-btn nd1-btn nd1-btn--outline nd1-job-detail__toolbar-save${
              isSaved ? " nd1-job-detail__toolbar-save--active" : ""
            }`}
            aria-pressed={isSaved}
            disabled={saveDisabled}
            onClick={handleSave}
          >
            <Heart
              className="nd1-icon nd1-job-detail__toolbar-save-icon"
              size={18}
              strokeWidth={1.75}
              fill="currentColor"
              aria-hidden="true"
            />
            <span>{isSaved ? "Saved" : "Save"}</span>
          </button>
        ) : (
          <Link
            to={guestSignInHref}
            className="nd1-job-detail__toolbar-btn nd1-btn nd1-btn--outline nd1-job-detail__toolbar-save"
          >
            <Heart
              className="nd1-icon nd1-job-detail__toolbar-save-icon"
              size={18}
              strokeWidth={1.75}
              fill="currentColor"
              aria-hidden="true"
            />
            <span>Save</span>
          </Link>
        )}

        <JobShareMenu job={job} className="nd1-job-detail__toolbar-share" />
      </div>

      <JobHighlightPills job={job} variant="detail" />
    </header>
  );
}
